/**
 * 请求上下文注入策略（hotel-hub + SDK 共用，单一真相源）
 *
 * 集中托管所有「从全局态推导、注入到每个业务请求」的策略，消除两个 client
 * （主应用 axios / SDK 裸 fetch）之间的逻辑漂移：
 * - 跳过名单：/hotel/info 自身是酒店信息来源，鸡生蛋，不触发 ensureHotel/resolveHotel
 * - 上下文头：由 buildContextHeaders 统一产出（第 4 步起仅 Authorization--租户/品牌/hotelId 由 BFF 服务端推导）
 *
 * 纯函数 + 仅依赖 @/lib/shared-storage，不引 axios/zustand，
 * 故 SDK 独立 bundle 可安全引用（与 shared-storage 同一约束）。
 */
import { readUserToken } from '@/lib/shared-storage';

/** 不触发酒店解析（ensureHotel）的接口；hotel/info 自身是酒店信息来源。
 *  BFF 同源路径 /api/hotel/info 命中此名单，避免 ensureHotel 经拦截器自举递归。 */
export const HOTEL_INFO_SKIP_URLS = ['/api/hotel/info'];

/** 判断该请求是否跳过酒店上下文解析（hotel/info 自举等） */
export function shouldSkipHotelContext(url: string | undefined): boolean {
  if (!url) return false;
  return HOTEL_INFO_SKIP_URLS.some((u) => url.includes(u));
}

/**
 * 构造请求上下文头（hotel-hub + SDK 共用，单一扩展点）。
 *
 * 第 4 步起仅产出 Authorization：
 *   - X-Tenant-Id / X-Brand-Id / hotelId 已下线--BFF callUpstream 服务端按域名 -> hotel/info
 *     推导（覆盖客户端，不信任客户端，客户端不可伪造租户/品牌）。
 *   - Authorization 保留：BFF whapi 端点（secret none）透传客户端
 *     token 给 whapi；readUserToken 在 SSR 返回 null（安全）。
 *
 * 扩展点：新增任何上下文头（locale / channel / app-version 等）只需在此函数内加一行，
 * 两个 client 自动生效，无需改动各自传输层。
 */
export function buildContextHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};

  const token = readUserToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  return headers;
}
